import { Box, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import DeleteIcon from '@mui/icons-material/Delete';
import { Question } from '../database/theSource';
import { QuestionStatus } from './constants/QuestionStatus';
import QuestionStatusChip from './chips/QuestionStatusChip';
import { ResponseType } from './constants/ResponseType';
import AnswerPromptCard from './AnswerPromptCard';
import QuestionCostChip from './chips/QuestionCostChip';
import { useAuth } from '../auth/Auth';
import { useRequest } from '../api/Request';
import { useApi } from '../api/Api';
import QuestionTimerChip from './chips/QuestionExpirationTimerChip';
import { getConfig } from '../config';
import IsPrivateChip from './chips/IsPrivateChip';
import LikeButton from '../components/LikeButton';
import UserHeader from '../components/UserHeader';
import { SubscriptionStatus, User } from '../database/user';
import { useCache } from '../api/cache/Cache';
import { LikeParentType } from './constants/LikeParentType';
import VideoPlayer from '../bts/components/video/VideoPlayer';

const theSourceMediaBucket = getConfig().media.theSourceMediaBucket;

interface QuestionCardProps {
    artist: User;
    question: Question;
    onDelete: (question: Question) => void;
    onSave: (question: Question) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
    artist,
    question,
    onDelete,
    onSave,
}) => {
    const theme = useTheme();
    const auth = useAuth();
    const user = auth.user;
    const deleteQuestionRequest = useRequest();
    const api = useApi();
    const cache = useCache();

    const handleDelete = (question: Question) => {
        deleteQuestionRequest.onStart();
        api.deleteQuestion(question)
            .then((response) => {
                onDelete(response.data);
                deleteQuestionRequest.onSuccess('Successfully deleted request');
            })
            .catch((err) => {
                console.error('Delete request error: ', err);
                deleteQuestionRequest.onFailure(err);
            });
    };

    const renderAnswerContent = () => {
        if (question.status !== QuestionStatus.Answered || !question.answer) {
            return null;
        }
        switch (question.responseType) {
            case ResponseType.Text: {
                return (
                    <Stack spacing={3}>
                        <Divider
                            sx={{
                                height: '4px',
                                borderStyle: 'dashed',
                                borderColor: theme.palette.primary.main,
                            }}
                        />
                        <UserHeader
                            timestamp={question.answer.createdAt}
                            username={artist.username}
                            subscriptionStatus={artist.subscriptionStatus}
                            subscriptionOverride={artist.subscriptionOverride}
                            displayName={artist.displayName}
                            isArtist={artist.isArtist}
                        />
                        <Typography
                            paddingTop='10px'
                            paddingLeft='25px'
                            paddingRight='25px'
                        >
                            {question.answer.content}
                        </Typography>
                    </Stack>
                );
            }
            case ResponseType.Video: {
                return (
                    <Stack spacing={3}>
                        <Divider
                            sx={{
                                height: '4px',
                                borderStyle: 'dashed',
                                borderColor: theme.palette.primary.main,
                            }}
                        />
                        <UserHeader
                            timestamp={question.answer.createdAt}
                            username={artist.username}
                            subscriptionStatus={artist.subscriptionStatus}
                            subscriptionOverride={artist.subscriptionOverride}
                            displayName={artist.displayName}
                            isArtist={artist.isArtist}
                        />
                        <VideoPlayer
                            src={
                                question.previewUrl
                                    ? question.previewUrl
                                    : `${theSourceMediaBucket}/${question.owner}/${question.createdAt}`
                            }
                        />
                    </Stack>
                );
            }
            default: {
                return (
                    <Typography>Could not obtain the response to this request</Typography>
                );
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
        >
            <Box
                sx={{
                    boxShadow: `0 0 4px 4px rgba(0, 0, 0, 0.16)`,
                    borderRadius: '16px',
                    borderColor: `${theme.palette.primary.main}`,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    padding: 2,
                    transition: 'box-shadow 0.25s ease',
                    '&:hover': {
                        boxShadow: `0 0 12px 4px rgba(139, 0, 0, 0.25)`,
                    },
                }}
            >
                <Stack spacing={2}>
                    <Stack
                        direction='row'
                        alignItems='flex-start'
                        justifyContent='space-between'
                    >
                        <UserHeader
                            timestamp={question.createdAt}
                            username={question.owner}
                            subscriptionStatus={
                                cache.userSummaries.get(question.owner)
                                    ?.subscriptionStatus || SubscriptionStatus.FREE_TIER
                            }
                            subscriptionOverride={
                                cache.userSummaries.get(question.owner)
                                    ?.subscriptionOverride
                            }
                            displayName={
                                cache.userSummaries.get(question.owner)?.displayName
                            }
                            isArtist={cache.userSummaries.get(question.owner)?.isArtist}
                        />
                        {user &&
                            user.isArtist &&
                            question.status === QuestionStatus.Pending && (
                                <IconButton
                                    onClick={() => handleDelete(question)}
                                    style={{
                                        color: `${theme.palette.primary.main}`,
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                    </Stack>

                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        {user &&
                            user.isArtist &&
                            question.status === QuestionStatus.Pending && (
                                <QuestionTimerChip createdAt={question.createdAt} />
                            )}
                        {user && (user.isArtist || user.username === question.owner) && (
                            <QuestionCostChip cost={question.cost} user={user} />
                        )}
                        <QuestionStatusChip status={question.status as QuestionStatus} />
                        <IsPrivateChip isPrivate={question.isPrivate} />
                    </Stack>
                </Stack>

                <Stack spacing={3} paddingTop='20px'>
                    <Typography
                        sx={{
                            paddingLeft: '25px',
                            paddingRight: '25px',
                            overflowWrap: 'break-word',
                        }}
                    >
                        {question.content}
                    </Typography>
                    {renderAnswerContent()}

                    <AnswerPromptCard question={question} onSave={onSave} />
                    <LikeButton<Question>
                        parentType={LikeParentType.Question}
                        pk={question.owner}
                        sk={question.createdAt}
                        likes={question.likes}
                        onEdit={onSave}
                    />
                </Stack>
            </Box>
        </motion.div>
    );
};

export default QuestionCard;
